import Phaser from "phaser";
import { ROUND_COMPLETE_EVENT, ROUND_EXIT_EVENT, ROUND_READY_EVENT, type RoundResult } from "./types";
import { SoundBus } from "./sound";
import { moormennersTheme } from "../theme/moormennersTheme";
import type {
  BackgroundLayerDefinition,
  GameTheme,
  SceneryHitZone,
  SoundKey,
  SpawnEvent,
  SpriteSheetDefinition,
  TargetHitBox,
  TargetDefinition,
  WorldSpriteDefinition
} from "../theme/types";

const ammoShellX = 710;
const ammoShellY = 520;
const ammoShellSpacing = 28;
const windmillDeathPoseFrameRate = 45;
const windmillDeathPoseFrames = 18;

type SceneData = {
  theme?: GameTheme;
  active?: boolean;
  windmillDebug?: boolean;
};

type ActiveTarget = {
  id: number;
  definition: TargetDefinition;
  asset: SpriteSheetDefinition;
  deathAsset?: SpriteSheetDefinition;
  sprite: Phaser.GameObjects.Sprite;
  worldX: number;
  worldY: number;
  baseY: number;
  velocityX: number;
  velocityY: number;
  direction: 1 | -1;
  wobble: number;
  verticalTurnMs: number;
  fixedFrameOffset?: number;
  frameSequenceIndex?: number;
  frameSequenceElapsedMs?: number;
  screenX?: number;
  spawnedAtMs: number;
  removeTimer?: Phaser.Time.TimerEvent;
  attachedTo?: ActiveTarget;
  falling?: boolean;
};

type BackgroundLayer = {
  definition: BackgroundLayerDefinition;
  image: Phaser.GameObjects.Image;
};

type WorldSprite = {
  definition: WorldSpriteDefinition;
  object: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
};

type Decal = {
  layerX: number;
  layerId?: string;
  image: Phaser.GameObjects.Image;
};

type Hud = {
  shells: Phaser.GameObjects.Image[];
  text: Record<string, Phaser.GameObjects.Text>;
};

export class GameScene extends Phaser.Scene {
  private theme: GameTheme = moormennersTheme;
  private soundBus?: SoundBus;
  private roundActive = false;
  private paused = false;
  private cameraX = 0;
  private elapsedMs = 0;
  private spawnElapsed = 0;
  private nextSpawnMs = 0;
  private score = 0;
  private ammo = 0;
  private hits = 0;
  private misses = 0;
  private shotsFired = 0;
  private reloads = 0;
  private targetSequence = 0;
  private roundRunId = 0;
  private reloading = false;
  private reloadReadyAt = 0;
  private lowTimeWarningPlayed = false;
  private targetHits: Record<string, number> = {};
  private spentScenery = new Set<string>();
  private spawnedOnceTargets = new Set<string>();
  private activeTargets: ActiveTarget[] = [];
  private backgroundLayers: BackgroundLayer[] = [];
  private worldSprites: WorldSprite[] = [];
  private decals: Decal[] = [];
  private hud?: Hud;
  private hudBitmapValues: Record<string, string> = {};
  private crosshair?: Phaser.GameObjects.Sprite;
  private cursorKeys?: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyA?: Phaser.Input.Keyboard.Key;
  private keyD?: Phaser.Input.Keyboard.Key;
  private keyP?: Phaser.Input.Keyboard.Key;
  private pauseOverlay?: Phaser.GameObjects.Container;
  private planeSpawned = false;
  private windmillDebug = false;

  constructor() {
    super("GameScene");
  }

  init(data: SceneData) {
    this.theme = data.theme ?? moormennersTheme;
    this.roundActive = data.active ?? false;
    this.windmillDebug = data.windmillDebug ?? false;
    this.soundBus = new SoundBus(this.theme);
  }

  preload() {
    if (!this.roundActive) return;

    for (const layer of this.theme.backgroundLayers) {
      this.loadImage(layer.key, layer.url);
    }

    for (const sprite of this.theme.worldSprites) {
      if (sprite.frameWidth && sprite.frameHeight) {
        this.loadSpriteSheet(sprite.key, sprite.url, {
          frameWidth: sprite.frameWidth,
          frameHeight: sprite.frameHeight
        });
      } else {
        this.loadImage(sprite.key, sprite.url);
      }
    }

    for (const target of this.theme.targets) {
      for (const asset of targetAssets(target)) {
        this.loadTargetSheet(asset);
      }
      for (const asset of targetDeathAssets(target)) {
        this.loadTargetSheet(asset);
      }
      if (target.deathAsset) this.loadTargetSheet(target.deathAsset);
      if (target.hitAsset) this.loadTargetSheet(target.hitAsset);
    }

    this.loadTargetSheet(this.theme.crosshair);
    this.loadTargetSheet(this.theme.effects.particle);
    this.loadImage("bullet-hole", this.theme.effects.bulletHoleUrl);
    this.loadImage("tree-hole", this.theme.effects.treeHoleUrl);
    this.loadImage("ammo-shell", this.theme.ui.ammoShellUrl);
    this.loadSpriteSheet("ammo-reload", this.theme.ui.ammoReloadUrl, {
      frameWidth: 112,
      frameHeight: 84
    });
  }

  create() {
    if (!this.roundActive) return;

    this.input.setDefaultCursor("none");
    this.input.mouse?.disableContextMenu();

    this.createAnimations();
    this.createBackground();
    this.createWorldSprites();
    this.createInput();
    this.createKeyboard();
    this.createCrosshair();
    this.createPauseOverlay();
    this.updateCameraLayers();

    if (this.roundActive) {
      this.startRound();
    }
  }

  update(time: number, delta: number) {
    this.updateCrosshair();

    if (!this.roundActive) return;

    if (this.paused) {
      return;
    }

    this.updateCamera(delta);
    this.updateReload(time);

    this.elapsedMs += delta;
    this.spawnElapsed += delta;

    this.updateSpawns(delta);

    this.updateTargets(time, delta);
    this.updateDecals();
    this.updateHud();

    if (!this.lowTimeWarningPlayed && this.theme.round.durationMs - this.elapsedMs <= 10_000) {
      this.lowTimeWarningPlayed = true;
      this.soundBus?.play("timeWarning");
    }

    if (this.elapsedMs >= this.theme.round.durationMs) {
      this.finishRound();
    }
  }

  private startRound() {
    this.roundRunId += 1;
    this.destroyTargets();
    this.destroyDecals();
    this.destroyHud();

    this.roundActive = true;
    this.paused = false;
    this.cameraX = 0;
    this.elapsedMs = 0;
    this.spawnElapsed = 0;
    this.nextSpawnMs = 250;
    this.score = 0;
    this.ammo = this.theme.round.magazineSize;
    this.hits = 0;
    this.misses = 0;
    this.shotsFired = 0;
    this.reloads = 0;
    this.targetSequence = 0;
    this.reloading = false;
    this.lowTimeWarningPlayed = false;
    this.planeSpawned = false;
    if (this.windmillDebug) {
      this.cameraX = this.windmillDebugCameraX();
    }
    this.spentScenery.clear();
    this.spawnedOnceTargets.clear();
    this.targetHits = Object.fromEntries([
      ...this.theme.targets.map((target) => [target.id, 0] as const),
      ...this.theme.sceneryHitZones.map((zone) => [zone.id, 0] as const)
    ]);
    this.pauseOverlay?.setVisible(false);
    this.resetWorldSpriteFrames();
    this.soundBus?.stopAllLoops();
    this.soundBus?.play("start");
    this.soundBus?.playLoop("ambient");
    this.createHud();
    this.spawnFixedTargets();
    this.updateCameraLayers();
    this.updateHud();
    window.dispatchEvent(new Event(ROUND_READY_EVENT));
  }

  private createInput() {
    this.input.off("pointerdown");
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.roundActive) return;

      if (this.paused) {
        this.togglePause();
        return;
      }

      if (pointer.rightButtonDown()) {
        this.startReload();
        return;
      }

      this.shoot(pointer.x, pointer.y);
    });
  }

  private createKeyboard() {
    this.cursorKeys = this.input.keyboard?.createCursorKeys();
    this.keyA = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keyP = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.P);

    this.input.keyboard?.off("keydown-R");
    this.input.keyboard?.off("keydown-P");
    this.input.keyboard?.off("keydown-ENTER");
    this.input.keyboard?.off("keydown-ESC");
    this.input.keyboard?.on("keydown-R", () => this.startReload());
    this.input.keyboard?.on("keydown-P", () => this.togglePause());
    this.input.keyboard?.on("keydown-ENTER", () => {
      if (this.roundActive && this.paused) this.restartRound();
    });
    this.input.keyboard?.on("keydown-ESC", () => {
      if (this.roundActive) this.exitRound();
    });
  }

  private shoot(screenX: number, screenY: number) {
    if (this.reloading || this.ammo <= 0) {
      this.soundBus?.play("empty");
      this.flashAmmo();
      return;
    }

    this.ammo -= 1;
    this.shotsFired += 1;
    this.soundBus?.play("shoot");
    this.spawnShellDiscard(this.ammo);
    this.flashCrosshair();

    const target = this.findHitTarget(screenX, screenY);

    if (target) {
      this.registerTargetHit(target, screenX, screenY);
      return;
    }

    const scenery = this.findSceneryHit(screenX, screenY);

    if (scenery) {
      this.registerSceneryHit(scenery, screenX, screenY);
      return;
    }

    this.misses += 1;
  }

  private registerTargetHit(target: ActiveTarget, screenX: number, screenY: number) {
    const points = this.pointsForTargetHit(target);

    this.hits += 1;
    this.addScore(points);
    this.targetHits[target.definition.id] = (this.targetHits[target.definition.id] ?? 0) + 1;
    if (target.definition.behavior === "planeCompanion") {
      this.playTargetHitSound(target.definition);
      this.dropPlaneCompanion(target);
      if (points !== 0) {
        this.spawnFloatingText(screenX, screenY, String(points), points < 0 ? this.theme.ui.colors.danger : this.theme.ui.colors.accent);
      }
      return;
    }
    if (target.definition.behavior !== "plane") {
      this.playTargetHitSound(target.definition);
    }
    this.spawnDeathAnimation(target);
    if (points !== 0) {
      this.spawnFloatingText(screenX, screenY, String(points), points < 0 ? this.theme.ui.colors.danger : this.theme.ui.colors.accent);
    }
    this.removeTarget(target, { respawnFixed: true });
  }

  private pointsForTargetHit(target: ActiveTarget) {
    const points = target.definition.points;
    if (!Array.isArray(points)) return points;

    const frame = this.currentFrameIndex(target.sprite);
    const phase = Math.min(points.length - 1, Math.floor(frame * points.length / target.asset.frames));
    return points[phase] ?? points[points.length - 1] ?? 0;
  }

  private registerSceneryHit(zone: SceneryHitZone, screenX: number, screenY: number) {
    if (zone.id === "signpost") {
      this.hitSignpost(zone, screenX, screenY);
      return;
    }

    if (zone.id === "field-scarecrow") {
      this.hitFieldScarecrow(zone, screenX, screenY);
      return;
    }

    if (zone.sound) {
      this.soundBus?.play(zone.sound);
    }

    this.targetHits[zone.id] = (this.targetHits[zone.id] ?? 0) + 1;

    this.misses += 1;

    if (zone.decal) {
      this.spawnBulletHole(zone, screenX, screenY);
    }

    if (zone.id.includes("tree")) {
      this.spawnTreeLeaves(zone, screenX, screenY);
    }

    if (zone.penalty) {
      this.addScore(zone.penalty);
      this.spawnFloatingText(screenX, screenY, String(zone.penalty), this.theme.ui.colors.danger);
    }
  }

  private findHitTarget(screenX: number, screenY: number) {
    const sorted = [...this.activeTargets].sort((a, b) => b.sprite.depth - a.sprite.depth);

    return sorted.find((target) => {
      if (!target.sprite.visible) return false;
      if (target.falling) return false;
      if (!this.targetFrameIsHittable(target)) return false;
      if (this.targetExcludedHitBoxContains(target, screenX, screenY)) return false;

      if (target.definition.hitBox) {
        return this.targetHitBoxContains(target, screenX, screenY);
      }

      if (this.alphaHitTest(target.sprite, screenX, screenY)) {
        return true;
      }

      if (target.definition.behavior === "fixed"
        || target.definition.behavior === "popup"
        || target.definition.behavior === "plane"
        || target.definition.behavior === "planeCompanion") {
        return false;
      }

      const dx = screenX - target.sprite.x;
      const dy = screenY - target.sprite.y;
      const radius = target.definition.hitRadius * Math.max(target.sprite.scaleX, 0.25);
      return dx * dx + dy * dy <= radius * radius;
    });
  }

  private targetFrameIsHittable(target: ActiveTarget) {
    const ranges = target.definition.hittableFrames;
    if (!ranges) return true;

    const frame = this.currentFrameIndex(target.sprite);
    return ranges.some(([start, end]) => frame >= start && frame <= end);
  }

  private targetHitBoxContains(target: ActiveTarget, screenX: number, screenY: number) {
    const hitBox = target.definition.hitBox;
    if (!hitBox) return false;

    return this.targetLocalBoxContains(target, hitBox, screenX, screenY);
  }

  private targetExcludedHitBoxContains(target: ActiveTarget, screenX: number, screenY: number) {
    return target.definition.excludedHitBoxes?.some((hitBox) => (
      this.targetLocalBoxContains(target, hitBox, screenX, screenY)
    )) ?? false;
  }

  private targetLocalBoxContains(target: ActiveTarget, hitBox: TargetHitBox, screenX: number, screenY: number) {
    const frame = target.sprite.frame;
    const scaleX = Math.abs(target.sprite.scaleX) || 1;
    const scaleY = Math.abs(target.sprite.scaleY) || 1;
    const topLeftX = target.sprite.x - frame.width * target.sprite.originX * scaleX;
    const topLeftY = target.sprite.y - frame.height * target.sprite.originY * scaleY;
    const localOffsetX = target.sprite.flipX
      ? frame.width - hitBox.offsetX - hitBox.width
      : hitBox.offsetX;
    const x = topLeftX + localOffsetX * scaleX;
    const y = topLeftY + hitBox.offsetY * scaleY;

    return screenX >= x
      && screenX <= x + hitBox.width * scaleX
      && screenY >= y
      && screenY <= y + hitBox.height * scaleY;
  }

  private alphaHitTest(sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image, screenX: number, screenY: number) {
    const frame = sprite.frame;
    const scaleX = Math.abs(sprite.scaleX) || 1;
    const scaleY = Math.abs(sprite.scaleY) || 1;
    const originX = frame.width * sprite.originX;
    const originY = frame.height * sprite.originY;
    const flipX = "flipX" in sprite && sprite.flipX;
    let localX = (screenX - sprite.x) / scaleX + originX;
    const localY = (screenY - sprite.y) / scaleY + originY;

    if (flipX) {
      localX = frame.width - localX;
    }

    if (localX < 0 || localY < 0 || localX >= frame.width || localY >= frame.height) {
      return false;
    }

    const alpha = this.textures.getPixelAlpha(
      Math.floor(localX),
      Math.floor(localY),
      sprite.texture.key,
      frame.name
    );

    return alpha > 10;
  }

  private findSceneryHit(screenX: number, screenY: number) {
    return this.theme.sceneryHitZones
      .filter((zone) => this.sceneryZoneContains(zone, screenX, screenY))
      .sort((a, b) => b.depth - a.depth)[0];
  }

  private sceneryZoneContains(zone: SceneryHitZone, screenX: number, screenY: number) {
    const zoneScreenX = this.screenXForLayerX(zone.x, zone.layerId);

    if (
      screenX < zoneScreenX
      || screenX > zoneScreenX + zone.width
      || screenY < zone.y
      || screenY > zone.y + zone.height
    ) {
      return false;
    }

    const worldSprite = this.worldSprites.find((sprite) => sprite.definition.id === zone.id)?.object;
    if (worldSprite) {
      return this.sceneryFrameHitBoxContains(zone, worldSprite, screenX, screenY)
        && this.alphaHitTest(worldSprite, screenX, screenY);
    }

    const layer = this.backgroundLayers.find((item) => item.definition.id === zone.layerId)?.image;
    return layer ? this.alphaHitTest(layer, screenX, screenY) : true;
  }

  private sceneryFrameHitBoxContains(
    zone: SceneryHitZone,
    sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
    screenX: number,
    screenY: number
  ) {
    if (!(sprite instanceof Phaser.GameObjects.Sprite)) return true;

    const frameHitBoxes = zone.hitBoxesByFrame?.[this.currentFrameIndex(sprite)];
    if (!frameHitBoxes?.length) return true;

    const frame = sprite.frame;
    const scaleX = Math.abs(sprite.scaleX) || 1;
    const scaleY = Math.abs(sprite.scaleY) || 1;
    const localX = (screenX - (sprite.x - frame.width * sprite.originX * scaleX)) / scaleX;
    const localY = (screenY - (sprite.y - frame.height * sprite.originY * scaleY)) / scaleY;

    return frameHitBoxes.some((hitBox) => (
      localX >= hitBox.offsetX
      && localX <= hitBox.offsetX + hitBox.width
      && localY >= hitBox.offsetY
      && localY <= hitBox.offsetY + hitBox.height
    ));
  }

  private startReload() {
    if (!this.roundActive || this.paused || this.ammo > 0) return;

    this.reloading = false;
    this.reloads += 1;
    this.reloadReadyAt = 0;
    this.ammo = this.theme.round.magazineSize;
    this.soundBus?.play("reload");
    this.updateHud();
    this.flashAmmo();
  }

  private updateReload(time: number) {
    if (!this.reloading || time < this.reloadReadyAt) return;

    this.reloading = false;
    this.ammo = this.theme.round.magazineSize;
  }

  private togglePause() {
    if (!this.roundActive) return;

    this.paused = !this.paused;
    this.pauseOverlay?.setVisible(this.paused);
  }

  private restartRound() {
    if (!this.roundActive) return;
    this.startRound();
  }

  private finishRound() {
    if (!this.roundActive) return;

    this.roundActive = false;
    this.paused = false;
    this.reloading = false;
    this.roundRunId += 1;
    this.soundBus?.stopAllLoops();
    this.soundBus?.play("gameOver");

    const result: RoundResult = {
      score: this.score,
      accuracy: this.shotsFired === 0 ? 0 : this.hits / this.shotsFired,
      hits: this.hits,
      misses: this.misses,
      shotsFired: this.shotsFired,
      reloads: this.reloads,
      durationMs: Math.round(Math.min(this.elapsedMs, this.theme.round.durationMs)),
      themeId: this.theme.id,
      themeVersion: this.theme.version,
      targetHits: this.targetHits
    };

    window.dispatchEvent(new CustomEvent<RoundResult>(ROUND_COMPLETE_EVENT, { detail: result }));
  }

  private exitRound() {
    if (!this.roundActive) return;

    this.roundActive = false;
    this.paused = false;
    this.reloading = false;
    this.roundRunId += 1;
    this.pauseOverlay?.setVisible(false);
    this.destroyTargets();
    this.destroyDecals();
    this.destroyHud();
    this.soundBus?.stopAllLoops();
    window.dispatchEvent(new Event(ROUND_EXIT_EVENT));
  }

  private createBackground() {
    this.backgroundLayers = this.theme.backgroundLayers.map((definition) => ({
      definition,
      image: this.add.image(0, 0, this.textureKey(definition.key)).setOrigin(0, 0).setDepth(definition.depth)
    }));
  }

  private createWorldSprites() {
    this.worldSprites = this.theme.worldSprites.map((definition) => {
      const key = this.textureKey(definition.key);
      const object = definition.frameWidth && definition.frameHeight
        ? this.add.sprite(0, definition.y, key, definition.frame ?? 0)
        : this.add.image(0, definition.y, key);

      object
        .setOrigin(definition.originX ?? 0.5, definition.originY ?? 0.5)
        .setScale(definition.scale ?? 1)
        .setDepth(definition.depth);

      return { definition, object };
    });
  }

  private resetWorldSpriteFrames() {
    for (const { definition, object } of this.worldSprites) {
      if (object instanceof Phaser.GameObjects.Sprite) {
        object.setFrame(definition.frame ?? 0);
        object.stop();
      }
    }
  }

  private hitSignpost(zone: SceneryHitZone, screenX: number, screenY: number) {
    this.misses += 1;

    const item = this.worldSprites.find((sprite) => sprite.definition.id === zone.id);
    const sign = item?.object;
    const hitFrame = item?.definition.hitFrame ?? 1;
    const initialFrame = item?.definition.frame ?? 0;

    if (zone.sound) {
      this.soundBus?.play(zone.sound);
    }

    if (sign instanceof Phaser.GameObjects.Sprite) {
      sign.setFrame(this.currentFrameIndex(sign) === hitFrame ? initialFrame : hitFrame);
      this.targetHits[zone.id] = (this.targetHits[zone.id] ?? 0) + 1;
    }

    if (zone.penalty) {
      this.addScore(zone.penalty);
      this.spawnFloatingText(screenX, screenY, String(zone.penalty), this.theme.ui.colors.danger);
    }
  }

  private hitFieldScarecrow(zone: SceneryHitZone, screenX: number, screenY: number) {
    const item = this.worldSprites.find((sprite) => sprite.definition.id === zone.id);
    const scarecrow = item?.object;
    const hitFrame = item?.definition.hitFrame ?? 0;

    if (!item || !(scarecrow instanceof Phaser.GameObjects.Sprite)) {
      this.misses += 1;
      return;
    }

    if (this.spentScenery.has(zone.id) || this.currentFrameIndex(scarecrow) === hitFrame) {
      this.misses += 1;
      return;
    }

    this.spentScenery.add(zone.id);
    this.hits += 1;
    this.targetHits[zone.id] = (this.targetHits[zone.id] ?? 0) + 1;
    if (zone.sound) this.soundBus?.play(zone.sound);
    if (zone.points) {
      this.addScore(zone.points);
      this.spawnFloatingText(screenX, screenY, String(zone.points), this.theme.ui.colors.accent);
    }

    scarecrow.play({ key: this.animationKey(item.definition.key), startFrame: item.definition.frame ?? 0 }, true);
    scarecrow.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      scarecrow.setFrame(hitFrame);
    });
  }

  private addScore(points: number) {
    this.score = Math.max(0, this.score + points);
  }

  private hitWorldSprite(id: string) {
    const item = this.worldSprites.find((sprite) => sprite.definition.id === id);
    const object = item?.object;
    if (!item || !(object instanceof Phaser.GameObjects.Sprite) || !item.definition.frames) return;

    object.play(this.animationKey(item.definition.key));
    object.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      object.setFrame(item.definition.frame ?? 0);
    });
  }

  private updateCamera(delta: number) {
    const left = this.cursorKeys?.left.isDown || this.keyA?.isDown;
    const right = this.cursorKeys?.right.isDown || this.keyD?.isDown;
    const direction = (right ? 1 : 0) - (left ? 1 : 0);
    const maxCameraX = this.theme.round.worldWidth - this.theme.round.viewportWidth;

    this.cameraX = Phaser.Math.Clamp(
      this.cameraX + direction * this.theme.round.cameraSpeed * (delta / 1000),
      0,
      maxCameraX
    );

    this.updateCameraLayers();
  }

  private updateCameraLayers() {
    const cameraMax = this.theme.round.worldWidth - this.theme.round.viewportWidth;

    for (const { definition, image } of this.backgroundLayers) {
      image.x = -this.cameraX * this.layerScrollFactor(definition.id);
    }

    for (const { definition, object } of this.worldSprites) {
      object.x = this.screenXForLayerX(definition.x, definition.layerId);
    }

    for (const decal of this.decals) {
      decal.image.x = this.screenXForLayerX(decal.layerX, decal.layerId);
    }

    for (const target of this.activeTargets) {
      target.sprite.x = this.screenXForTarget(target);
    }
  }

  private updateSpawns(delta: number) {
    const gate = this.theme.round.spawnGate;

    if (!gate) {
      if (this.spawnElapsed >= this.nextSpawnMs && this.spawnCapacityUsed() < this.theme.round.maxActiveTargets) {
        this.spawnElapsed = 0;
        this.nextSpawnMs = randomInRange(this.theme.round.spawnIntervalMs);
        this.spawnTarget();
      }
      return;
    }

    const checkMs = 1000 / gate.checksPerSecond;
    while (this.spawnElapsed >= checkMs) {
      this.spawnElapsed -= checkMs;
      if (this.spawnCapacityUsed() >= this.theme.round.maxActiveTargets) continue;
      if (Math.floor(Math.random() * gate.chance) !== 0) continue;
      this.spawnOriginalEvent(gate.events[Math.floor(Math.random() * gate.events.length)] ?? "none", gate.normalTargetIds);
    }
  }

  private spawnOriginalEvent(event: SpawnEvent, normalTargetIds: string[]) {
    if (event === "none") return;

    if (event === "normal") {
      const id = normalTargetIds[Math.floor(Math.random() * normalTargetIds.length)];
      const definition = this.findTargetDefinition(id);
      if (definition && this.canSpawnTarget(definition)) this.spawnFlyingTarget(definition);
      return;
    }

    const definition = this.findTargetDefinition(event);
    if (!definition || !this.canSpawnTarget(definition)) return;

    if (definition.behavior === "popup") {
      this.spawnPopupTarget(definition);
      return;
    }

    if (definition.behavior === "plane") {
      this.spawnFlyingTarget(definition);
    }
  }

  private spawnTarget() {
    let definition = this.pickSpawnableTarget();

    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (this.canSpawnTarget(definition)) break;
      definition = this.pickSpawnableTarget();
    }

    if (!this.canSpawnTarget(definition)) return;

    if (definition.behavior === "popup") {
      this.spawnPopupTarget(definition);
      return;
    }

    if (definition.behavior === "fall") {
      this.spawnFallingTarget(definition);
      return;
    }

    this.spawnFlyingTarget(definition);
  }

  private pickSpawnableTarget() {
    return weightedPick(this.theme.targets.filter((target) => (
      target.behavior !== "fixed" && target.behavior !== "plane" && target.spawnWeight > 0
    )));
  }

  private spawnCapacityUsed() {
    return this.activeTargets.filter((target) => (
      target.definition.behavior !== "fixed"
        && target.definition.behavior !== "fall"
        && target.definition.behavior !== "planeCompanion"
    )).length;
  }

  private canSpawnTarget(definition: TargetDefinition) {
    if (definition.behavior === "fixed") return false;
    if (definition.oncePerRound && this.spawnedOnceTargets.has(definition.id)) return false;
    if (definition.behavior === "plane" && this.planeSpawned) return false;
    if (definition.behavior === "popup" && this.activeTargets.some((target) => target.definition.id === definition.id)) return false;
    return true;
  }

  private findTargetDefinition(id: string) {
    return this.theme.targets.find((target) => target.id === id);
  }

  private spawnFixedTargets() {
    for (const definition of this.theme.targets) {
      if (definition.behavior === "fixed") {
        this.spawnFixedTarget(definition);
      }
    }
  }

  private spawnFlyingTarget(definition: TargetDefinition) {
    const direction: 1 | -1 = Math.random() > 0.5 ? 1 : -1;
    const worldX = this.spawnWorldX(definition, direction);
    const target = this.createTarget(definition, worldX, randomInRange(definition.yRange));
    const speed = randomInRange(definition.speed);

    target.direction = direction;
    target.velocityX = speed * direction;
    target.velocityY = definition.behavior === "fly"
      ? this.nextFlightVelocity(target)
      : randomInRange(definition.velocityY ?? [0, 0]);
    target.sprite.setFlipX(direction === 1);
    if (definition.lifetimeMs && definition.behavior === "fly") {
      target.removeTimer = this.time.delayedCall(randomInRange(definition.lifetimeMs), () => {
        if (this.activeTargets.includes(target)) this.removeTarget(target);
      });
    }
    this.activeTargets.push(target);

    if (definition.behavior === "plane") {
      this.planeSpawned = true;
      this.soundBus?.playLoop("planeLoop");
      this.spawnPlaneCompanion(target);
    }
  }

  private spawnPopupTarget(definition: TargetDefinition) {
    const worldX = definition.screenXRange
      ? this.cameraX + randomInRange(definition.screenXRange)
      : definition.worldXRange
      ? randomInRange(definition.worldXRange)
      : this.cameraX + randomInRange([110, this.theme.round.viewportWidth - 110]);
    const worldY = definition.popupMode === "sheet" || definition.worldXRange
      ? randomInRange(definition.yRange)
      : this.theme.round.viewportHeight + 28;
    const target = this.createTarget(definition, worldX, worldY);

    if (definition.screenXRange) {
      target.screenX = worldX - this.cameraX;
    }

    if (definition.randomFlip) {
      target.sprite.setFlipX(Math.random() > 0.5);
    }
    this.soundBus?.play(definition.spawnSound ?? "bigChickenPop");
    this.activeTargets.push(target);
    if (definition.oncePerRound) {
      this.spawnedOnceTargets.add(definition.id);
    }

    if (definition.popupMode === "sheet" || definition.worldXRange) {
      if (definition.frameSequence?.length) return;

      target.sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        if (this.activeTargets.includes(target)) this.removeTarget(target);
      });
      return;
    }

    const lifetime = randomInRange(definition.lifetimeMs ?? [1500, 2600]);

    target.sprite.setOrigin(definition.originX ?? 0.5, definition.originY ?? 1);
    target.baseY = this.theme.round.viewportHeight - 4;
    this.tweens.add({
      targets: target,
      worldY: target.baseY,
      duration: 210,
      ease: "Sine.easeOut"
    });
    target.removeTimer = this.time.delayedCall(lifetime, () => {
      this.tweens.add({
        targets: target,
        worldY: this.theme.round.viewportHeight + 34,
        duration: 180,
        ease: "Sine.easeIn",
        onComplete: () => this.removeTarget(target)
      });
    });
  }

  private spawnFixedTarget(definition: TargetDefinition) {
    if (!definition.fixedPosition) return;

    for (const frameOffset of definition.fixedFrameOffsets ?? [0]) {
      this.spawnFixedTargetInstance(definition, frameOffset);
    }
  }

  private spawnFixedTargetInstance(definition: TargetDefinition, frameOffset: number) {
    if (!definition.fixedPosition) return;
    if (this.activeTargets.some((target) => (
      target.definition.id === definition.id && target.fixedFrameOffset === frameOffset
    ))) {
      return;
    }

    const target = this.createTarget(definition, definition.fixedPosition.x, definition.fixedPosition.y, frameOffset);
    target.fixedFrameOffset = frameOffset;
    this.activeTargets.push(target);
  }

  private spawnFallingTarget(definition: TargetDefinition) {
    const target = this.createTarget(
      definition,
      this.cameraX + randomInRange([80, this.theme.round.viewportWidth - 80]),
      randomInRange(definition.yRange)
    );

    target.velocityX = randomInRange([-22, 22]);
    target.velocityY = randomInRange(definition.speed);
    this.activeTargets.push(target);
  }

  private spawnWorldX(definition: TargetDefinition, direction: 1 | -1) {
    if (definition.spawnXRanges) {
      return randomInRange(direction === 1 ? definition.spawnXRanges.left : definition.spawnXRanges.right);
    }

    const screenX = direction === 1 ? -80 : this.theme.round.viewportWidth + 80;
    return this.cameraX * this.layerScrollFactor(definition.layerId) + screenX;
  }

  private createTarget(definition: TargetDefinition, worldX: number, worldY: number, frameOffset = 0): ActiveTarget {
    const variant = randomTargetAsset(definition);
    const asset = variant.asset;
    const sprite = this.add.sprite(worldX - this.cameraX, worldY, this.textureKey(asset.key))
      .setScale(definition.scale)
      .setDepth(definition.depth)
      .setOrigin(definition.originX ?? 0.5, definition.originY ?? 0.5);

    const frameSequence = definition.frameSequence;

    if (frameSequence?.length) {
      sprite.setFrame(frameSequence[0] ?? 0);
    } else if (definition.behavior === "fixed" || definition.behavior === "planeCompanion") {
      sprite.setFrame(frameOffset);
    } else {
      sprite.play({ key: this.animationKey(asset.key), startFrame: frameOffset });
    }

    return {
      id: this.targetSequence += 1,
      definition,
      asset,
      deathAsset: variant.deathAsset,
      sprite,
      worldX,
      worldY,
      baseY: worldY,
      velocityX: 0,
      velocityY: 0,
      direction: 1,
      wobble: Math.random() * Math.PI * 2,
      verticalTurnMs: randomInRange(definition.flight?.turnIntervalMs ?? [1000, 2900]),
      fixedFrameOffset: frameOffset,
      frameSequenceIndex: frameSequence?.length ? 0 : undefined,
      frameSequenceElapsedMs: frameSequence?.length ? 0 : undefined,
      spawnedAtMs: this.time.now
    };
  }

  private updateTargets(time: number, delta: number) {
    const dt = delta / 1000;

    for (const target of [...this.activeTargets]) {
      this.updateFrameSequence(target, delta);
      if (!this.activeTargets.includes(target)) continue;

      if (target.definition.behavior === "fly") {
        target.worldX += target.velocityX * dt;
        target.verticalTurnMs -= delta;
        if (target.verticalTurnMs <= 0) {
          target.velocityY = this.nextFlightVelocity(target);
          target.verticalTurnMs = randomInRange(target.definition.flight?.turnIntervalMs ?? [1000, 2900]);
        }
        target.worldY += target.velocityY * dt;
        this.keepFlyingTargetInBounds(target);
        target.sprite.rotation = Phaser.Math.Clamp(target.velocityY / 520, -0.08, 0.08);
      }

      if (target.definition.behavior === "plane") {
        target.worldX += target.velocityX * dt;
        target.worldY += target.velocityY * dt;
        target.sprite.rotation = Math.sin(time / 240 + target.wobble) * 0.05;
      }

      if (target.definition.behavior === "planeCompanion") {
        this.updatePlaneCompanion(target, dt);
        if (!this.activeTargets.includes(target)) continue;
      }

      if (target.definition.behavior === "fall") {
        target.worldX += target.velocityX * dt;
        target.worldY += target.velocityY * dt;
        target.sprite.rotation += dt * 1.7;
      }

      if (target.definition.behavior === "fixed") {
        const frameDuration = 1000 / target.asset.frameRate;
        const animationTime = target.definition.fixedFrameOffsets ? time : time - target.spawnedAtMs;
        const frame = (target.fixedFrameOffset ?? 0) + Math.floor(animationTime / frameDuration);
        target.sprite.setFrame(frame % target.asset.frames);
      }

      target.sprite.x = this.screenXForTarget(target);
      target.sprite.y = target.worldY;

      if (target.definition.behavior === "fly"
        && (
          target.sprite.x < -180
          || target.sprite.x > this.theme.round.viewportWidth + 180
          || target.worldY < -110
          || target.worldY > this.theme.round.viewportHeight
        )) {
        this.removeTarget(target);
      }

      if (target.definition.behavior === "plane" && this.isPlaneOutOfBounds(target)) {
        this.removeTarget(target);
      }

      if (target.definition.behavior === "fall" && target.sprite.y > this.theme.round.viewportHeight + 120) {
        this.removeTarget(target);
      }

      if (target.definition.behavior === "planeCompanion" && target.worldY >= 400) {
        this.removeTarget(target);
      }
    }
  }

  private spawnPlaneCompanion(plane: ActiveTarget) {
    const id = plane.definition.companionTargetId;
    if (!id) return;

    const definition = this.findTargetDefinition(id);
    if (!definition) return;

    const target = this.createTarget(definition, plane.worldX, plane.worldY);
    target.attachedTo = plane;
    this.updatePlaneCompanion(target, 0);
    this.activeTargets.push(target);
  }

  private updatePlaneCompanion(target: ActiveTarget, dt: number) {
    if (target.falling) {
      target.worldY += target.velocityY * dt;
      return;
    }

    const plane = target.attachedTo;
    if (!plane || !this.activeTargets.includes(plane)) {
      this.removeTarget(target);
      return;
    }

    target.worldX = plane.worldX + (plane.velocityX < 0 ? 70 : -120);
    target.worldY = plane.worldY;
  }

  private dropPlaneCompanion(target: ActiveTarget) {
    target.falling = true;
    target.attachedTo = undefined;
    target.velocityX = 0;
    target.velocityY = randomInRange(target.definition.speed);
  }

  private updateFrameSequence(target: ActiveTarget, delta: number) {
    const sequence = target.definition.frameSequence;
    if (!sequence?.length) return;

    const frameDuration = 1000 / (target.definition.frameSequenceRate ?? target.asset.frameRate);
    target.frameSequenceElapsedMs = (target.frameSequenceElapsedMs ?? 0) + delta;

    while ((target.frameSequenceElapsedMs ?? 0) >= frameDuration) {
      target.frameSequenceElapsedMs = (target.frameSequenceElapsedMs ?? 0) - frameDuration;
      target.frameSequenceIndex = (target.frameSequenceIndex ?? 0) + 1;

      if (target.frameSequenceIndex >= sequence.length) {
        this.removeTarget(target);
        return;
      }

      target.sprite.setFrame(sequence[target.frameSequenceIndex] ?? 0);
    }
  }

  private isPlaneOutOfBounds(target: ActiveTarget) {
    const [minX, maxX] = target.definition.despawnXRange ?? [-180, this.theme.round.worldWidth + 180];
    return (target.direction === 1 && target.worldX > maxX)
      || (target.direction === -1 && target.worldX < minX)
      || target.worldY > this.theme.round.viewportHeight + 80;
  }

  private nextFlightVelocity(target: ActiveTarget) {
    const flight = target.definition.flight;
    if (!flight) return 0;

    const [minY, maxY] = flight.boundsY;
    const velocity = randomInRange(flight.verticalVelocity);

    if (target.worldY <= minY + 24) return Math.abs(velocity);
    if (target.worldY >= maxY - 24) return -Math.abs(velocity || flight.verticalVelocity[1]);
    return velocity;
  }

  private keepFlyingTargetInBounds(target: ActiveTarget) {
    const bounds = target.definition.flight?.boundsY;
    if (!bounds) return;

    if (target.worldY < bounds[0]) {
      target.worldY = bounds[0];
      target.velocityY = Math.abs(target.velocityY);
    }

    if (target.worldY > bounds[1]) {
      target.worldY = bounds[1];
      target.velocityY = -Math.abs(target.velocityY);
    }
  }

  private spawnDeathAnimation(target: ActiveTarget) {
    const asset = target.definition.hitAsset ?? target.deathAsset ?? target.definition.deathAsset;

    if (target.definition.behavior === "plane") {
      const plane = this.add.sprite(target.sprite.x, target.sprite.y, this.textureKey(target.asset.key))
        .setDepth(target.sprite.depth + 2)
        .setScale(target.sprite.scaleX, target.sprite.scaleY)
        .setFlipX(target.sprite.flipX)
        .setRotation(target.sprite.rotation);

      plane.play(this.animationKey(target.asset.key));
      this.soundBus?.play("planeFail");
      this.tweens.add({
        targets: plane,
        x: plane.x + (target.direction === 1 ? 240 : -240),
        y: plane.y + 470,
        rotation: plane.rotation + (target.direction === 1 ? 2.8 : -2.8),
        alpha: 0,
        duration: 1400,
        ease: "Sine.easeIn",
        onComplete: () => {
          this.soundBus?.play("planeCrash");
          plane.destroy();
        }
      });
      return;
    }

    if (!asset) {
      this.spawnParticles(target.sprite.x, target.sprite.y);
      return;
    }

    const death = this.add.sprite(target.sprite.x, target.sprite.y, this.textureKey(asset.key))
      .setDepth(target.sprite.depth + 2)
      .setScale(target.sprite.scaleX, target.sprite.scaleY)
      .setFlipX(target.sprite.flipX)
      .setRotation(target.sprite.rotation)
      .setOrigin(target.sprite.originX, target.sprite.originY);

    if (target.definition.behavior === "fixed") {
      this.playWrappedDeathFrames(target, death, asset);
      return;
    }

    if (target.definition.behavior === "fly") {
      death.play({
        key: this.animationKey(asset.key),
        startFrame: 0
      });

      const fallDistance = this.theme.round.viewportHeight + asset.frameHeight - death.y;
      const fallDuration = Phaser.Math.Clamp((fallDistance / 300) * 1000, 850, 2400);

      this.tweens.add({
        targets: death,
        x: death.x + target.velocityX * (fallDuration / 1000) * 0.35,
        y: this.theme.round.viewportHeight + asset.frameHeight,
        rotation: death.rotation + (target.direction === 1 ? 0.7 : -0.7),
        duration: fallDuration,
        ease: "Sine.easeIn",
        onComplete: () => death.destroy()
      });

      return;
    }

    death.play({
      key: this.animationKey(asset.key),
      startFrame: 0
    });
    death.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => death.destroy());

    if (target.definition.behavior === "popup") {
      this.tweens.add({
        targets: death,
        alpha: 0,
        duration: 700,
        delay: 180,
        onComplete: () => death.destroy()
      });
    }
  }

  private playWrappedDeathFrames(target: ActiveTarget, death: Phaser.GameObjects.Sprite, asset: SpriteSheetDefinition) {
    if (target.definition.id === "windmillChicken" && asset.key === "windmill-chicken-death") {
      this.playRotatingWindmillDeath(target, death, asset);
      return;
    }

    const fixedOffsets = target.definition.fixedFrameOffsets;
    if (fixedOffsets?.length && asset.frames % fixedOffsets.length === 0) {
      const segmentLength = asset.frames / fixedOffsets.length;
      const segmentIndex = Math.max(0, fixedOffsets.indexOf(target.fixedFrameOffset ?? 0));
      this.playDeathFrameSegment(death, asset, segmentIndex * segmentLength, segmentLength);
      return;
    }

    if (asset.frames === target.asset.frames * 2) {
      this.playDeathFrameSegment(death, asset, this.currentFrameIndex(target.sprite) * 2, 2);
      return;
    }

    const ratio = asset.frames / target.asset.frames;
    const frameStep = Number.isFinite(ratio) && ratio > 1 ? Math.floor(ratio) : 1;
    const startFrame = (this.currentFrameIndex(target.sprite) * frameStep) % asset.frames;
    const segmentLength = Number.isInteger(ratio) && ratio > 1
      ? frameStep
      : asset.frames;

    this.playDeathFrameSegment(death, asset, startFrame, segmentLength);
  }

  private playRotatingWindmillDeath(target: ActiveTarget, death: Phaser.GameObjects.Sprite, asset: SpriteSheetDefinition) {
    const liveFrame = this.currentFrameIndex(target.sprite);
    const segmentLength = Math.min(windmillDeathPoseFrames, asset.frames);
    const startRotation = Math.PI * 2 * liveFrame / target.asset.frames;
    const poseFrameDuration = 1000 / windmillDeathPoseFrameRate;
    const rotationRadiansPerMs = (Math.PI * 2 * target.asset.frameRate) / target.asset.frames / 1000;
    const startTime = this.time.now;
    let frameTimer: Phaser.Time.TimerEvent | undefined;

    death
      .setFrame(0)
      .setRotation(startRotation);

    frameTimer = this.time.addEvent({
      delay: 1000 / 60,
      loop: true,
      callback: () => {
        const elapsedMs = this.time.now - startTime;
        const frameIndex = Math.min(segmentLength - 1, Math.floor(elapsedMs / poseFrameDuration));
        death.setFrame(frameIndex);
        death.rotation = startRotation + elapsedMs * rotationRadiansPerMs;

        if (elapsedMs >= segmentLength * poseFrameDuration) {
          frameTimer?.remove(false);
          death.destroy();
        }
      }
    });
  }

  private playDeathFrameSegment(
    death: Phaser.GameObjects.Sprite,
    asset: SpriteSheetDefinition,
    startFrame: number,
    segmentLength: number,
    onFrame?: (sprite: Phaser.GameObjects.Sprite, frameIndex: number) => void
  ) {
    const frameDuration = 1000 / asset.frameRate;
    let frameIndex = 0;
    let frameTimer: Phaser.Time.TimerEvent | undefined;

    death.setFrame(startFrame);
    onFrame?.(death, frameIndex);
    frameTimer = this.time.addEvent({
      delay: frameDuration,
      loop: true,
      callback: () => {
        frameIndex += 1;
        if (frameIndex >= segmentLength) {
          frameTimer?.remove(false);
          death.destroy();
          return;
        }
        death.setFrame((startFrame + frameIndex) % asset.frames);
        onFrame?.(death, frameIndex);
      }
    });
  }

  private windmillDebugCameraX() {
    const target = this.theme.targets.find((item) => item.id === "windmillChicken");
    const maxCameraX = this.theme.round.worldWidth - this.theme.round.viewportWidth;
    if (!target?.fixedPosition) return 0;

    const scrollFactor = this.layerScrollFactor(target.layerId);
    return Phaser.Math.Clamp((target.fixedPosition.x - this.theme.round.viewportWidth * 0.605) / scrollFactor, 0, maxCameraX);
  }

  private spawnBulletHole(zone: SceneryHitZone, screenX: number, screenY: number) {
    const hole = this.add.image(screenX, screenY, this.textureKey("bullet-hole"))
      .setDepth(zone.depth)
      .setRotation(randomInRange([-0.2, 0.2]))
      .setAlpha(0.94);
    const decal = {
      layerX: this.cameraX * this.layerScrollFactor(zone.layerId) + screenX,
      layerId: zone.layerId,
      image: hole
    };

    this.decals.push(decal);
  }

  private updateDecals() {
    for (const decal of this.decals) {
      decal.image.x = this.screenXForLayerX(decal.layerX, decal.layerId);
    }
  }

  private spawnTreeLeaves(zone: SceneryHitZone, screenX: number, screenY: number) {
    const leafDefinition = this.theme.targets.find((target) => target.id === "leaf");
    if (!leafDefinition) return;

    const hitLayerX = this.cameraX * this.layerScrollFactor(zone.layerId) + screenX;
    const target = this.createTarget(
      leafDefinition,
      Math.max(zone.x + 20, hitLayerX + randomInRange([10, 80])),
      Phaser.Math.Clamp(screenY - randomInRange([40, 110]), -60, this.theme.round.viewportHeight - 100)
    );

    target.velocityX = randomInRange([80, 140]);
    target.velocityY = randomInRange([85, 130]);
    target.sprite
      .setDepth(zone.depth + 3)
      .setScale(randomInRange([0.74, 1]))
      .setRotation(randomInRange([-0.7, 0.7]));

    this.activeTargets.push(target);
  }

  private spawnParticles(screenX: number, screenY: number) {
    for (let index = 0; index < 7; index += 1) {
      const particle = this.add.sprite(screenX, screenY, this.textureKey(this.theme.effects.particle.key))
        .setDepth(210)
        .setScale(2);
      particle.play(this.animationKey(this.theme.effects.particle.key));
      this.tweens.add({
        targets: particle,
        x: screenX + randomInRange([-28, 28]),
        y: screenY + randomInRange([-28, 28]),
        alpha: 0,
        duration: 350,
        onComplete: () => particle.destroy()
      });
    }
  }

  private spawnFloatingText(screenX: number, screenY: number, text: string, _color: string) {
    const label = this.add.text(screenX, screenY, text, {
      fontFamily: '"Cherry Bomb One", "Arial Black", system-ui, sans-serif',
      fontSize: "48px",
      color: "#fff",
      stroke: "#050505",
      strokeThickness: 12,
      align: "center"
    })
      .setOrigin(0.5)
      .setDepth(230);

    this.tweens.add({
      targets: label,
      y: screenY - 42,
      alpha: 0,
      duration: 780,
      ease: "Sine.easeOut",
      onComplete: () => label.destroy()
    });
  }

  private removeTarget(target: ActiveTarget, options: { respawnFixed?: boolean } = {}) {
    this.activeTargets = this.activeTargets.filter((item) => item !== target);
    target.removeTimer?.remove(false);
    target.sprite.destroy();

    if (target.definition.behavior === "plane") {
      this.soundBus?.stopLoop("planeLoop");
      this.removePlaneCompanions(target);
    }

    if (options.respawnFixed && target.definition.behavior === "fixed" && target.definition.fixedRespawnMs) {
      const frameOffset = target.fixedFrameOffset ?? 0;
      const roundRunId = this.roundRunId;
      this.time.delayedCall(randomInRange(target.definition.fixedRespawnMs), () => {
        if (!this.roundActive || this.roundRunId !== roundRunId) return;
        this.spawnFixedTargetInstance(target.definition, frameOffset);
      });
    }
  }

  private removePlaneCompanions(plane: ActiveTarget) {
    for (const target of [...this.activeTargets]) {
      if (target.attachedTo !== plane) continue;
      if (target.falling) {
        target.attachedTo = undefined;
      } else {
        this.removeTarget(target);
      }
    }
  }

  private destroyTargets() {
    for (const target of this.activeTargets) {
      target.removeTimer?.remove(false);
      target.sprite.destroy();
    }
    this.activeTargets = [];
    this.soundBus?.stopLoop("planeLoop");
  }

  private destroyDecals() {
    for (const decal of this.decals) {
      decal.image.destroy();
    }
    this.decals = [];
  }

  private createHud() {
    const shells = Array.from({ length: this.theme.round.magazineSize }, (_, index) => (
      this.add.image(ammoShellX - index * ammoShellSpacing, ammoShellY, this.textureKey("ammo-shell"))
        .setDepth(240)
        .setOrigin(0, 0)
    ));
    this.hud = {
      shells,
      text: {
        score: this.createHudText(786, 46, "right"),
        time: this.createHudText(14, 46, "left")
      }
    };
    this.hudBitmapValues = {};
  }

  private updateHud() {
    if (!this.hud) return;

    const remaining = Math.max(0, this.theme.round.durationMs - this.elapsedMs);
    this.setHudText("score", String(this.score));
    this.setHudText("time", formatClock(remaining));

    for (let index = 0; index < this.hud.shells.length; index += 1) {
      this.hud.shells[index].setVisible(index < this.ammo);
    }
  }

  private destroyHud() {
    if (!this.hud) return;

    this.hud.shells.forEach((shell) => shell.destroy());
    Object.values(this.hud.text).forEach((text) => text.destroy());
    this.hud = undefined;
    this.hudBitmapValues = {};
  }

  private createHudText(x: number, y: number, align: "left" | "right") {
    return this.add.text(x, y, "", {
      fontFamily: '"Cherry Bomb One", "Arial Black", system-ui, sans-serif',
      fontSize: "56px",
      color: "#fff",
      stroke: "#050505",
      strokeThickness: 18,
      align
    })
      .setOrigin(align === "right" ? 1 : 0, 0.5)
      .setDepth(240);
  }

  private setHudText(id: string, text: string) {
    if (!this.hud || this.hudBitmapValues[id] === text) return;

    this.hudBitmapValues[id] = text;
    this.hud.text[id]?.setText(text);
  }

  private createCrosshair() {
    this.crosshair = this.add.sprite(0, 0, this.textureKey(this.theme.crosshair.key))
      .setDepth(300)
      .setOrigin(0.5)
      .setFrame(0);
  }

  private updateCrosshair() {
    if (!this.crosshair) return;

    const pointer = this.input.activePointer;
    this.crosshair.setPosition(pointer.x, pointer.y);
  }

  private createPauseOverlay() {
    const width = this.theme.round.viewportWidth;
    const height = this.theme.round.viewportHeight;
    const container = this.add.container(0, 0).setDepth(280).setVisible(false);
    const graphics = this.add.graphics();
    graphics.fillStyle(0x17120f, 0.62);
    graphics.fillRect(0, 0, width, height);
    const label = this.add.text(width / 2, height / 2, this.theme.ui.copy.paused, {
      fontFamily: '"Cherry Bomb One", "Arial Black", system-ui, sans-serif',
      fontSize: "64px",
      color: "#fff",
      stroke: "#050505",
      strokeThickness: 16,
      align: "center"
    })
      .setOrigin(0.5)
      .setDepth(281);

    container.add([graphics, label]);
    this.pauseOverlay = container;
  }

  private flashAmmo() {
    const shells = this.hud?.shells.filter((shell) => shell.visible);
    if (!shells?.length) return;

    this.tweens.add({
      targets: shells,
      scale: 1.04,
      yoyo: true,
      duration: 85
    });
  }

  private spawnShellDiscard(consumedShellIndex: number) {
    const shellIndex = Phaser.Math.Clamp(consumedShellIndex, 0, this.theme.round.magazineSize - 1);
    const startX = ammoShellX - shellIndex * ammoShellSpacing;
    const shell = this.add.sprite(ammoShellX - shellIndex * ammoShellSpacing, ammoShellY, this.textureKey("ammo-reload"))
      .setDepth(243)
      .setOrigin(0, 0);

    shell.play(this.animationKey("ammo-reload"));
    this.tweens.add({
      targets: shell,
      x: startX + randomInRange([36, 68]),
      y: ammoShellY - randomInRange([28, 54]),
      duration: 875,
      ease: "Sine.easeOut",
      onComplete: () => shell.destroy()
    });
  }

  private flashCrosshair() {
    this.crosshair?.setFrame(1);
    this.time.delayedCall(70, () => this.crosshair?.setFrame(0));
  }

  private playTargetHitSound(definition: TargetDefinition) {
    const sound = definition.hitSound;
    if (!sound) return;
    this.soundBus?.play(Array.isArray(sound) ? weightedPickSound(sound) : sound);
  }

  private loadTargetSheet(asset: SpriteSheetDefinition) {
    this.loadSpriteSheet(asset.key, asset.url, {
      frameWidth: asset.frameWidth,
      frameHeight: asset.frameHeight
    });
  }

  private loadImage(key: string, url: string) {
    const textureKey = this.textureKey(key);
    if (this.textures.exists(textureKey)) return;
    this.load.image(textureKey, url);
  }

  private loadSpriteSheet(key: string, url: string, frameConfig: Phaser.Types.Loader.FileTypes.ImageFrameConfig) {
    const textureKey = this.textureKey(key);
    if (this.textures.exists(textureKey)) return;
    this.load.spritesheet(textureKey, url, frameConfig);
  }

  private createAnimations() {
    const worldSpriteSheets = this.theme.worldSprites
      .filter((sprite) => sprite.frameWidth && sprite.frameHeight && sprite.frames)
      .map((sprite) => ({
        key: sprite.key,
        url: sprite.url,
        frameWidth: sprite.frameWidth as number,
        frameHeight: sprite.frameHeight as number,
        frames: sprite.frames as number,
        frameRate: sprite.frameRate ?? 10,
        repeat: sprite.repeat ?? 0
      }));
    const sheets = [
      this.theme.crosshair,
      this.theme.effects.particle,
      ...worldSpriteSheets,
      ...this.theme.targets.flatMap((target) => [
        ...targetAssets(target),
        ...targetDeathAssets(target),
        target.deathAsset,
        target.hitAsset
      ].filter(Boolean) as SpriteSheetDefinition[])
    ];

    for (const sheet of sheets) {
      this.createAnimation(sheet);
    }

    this.recreateAnimation(this.animationKey("ammo-reload"), {
      key: this.animationKey("ammo-reload"),
      frames: this.anims.generateFrameNumbers(this.textureKey("ammo-reload"), { start: 0, end: 20 }),
      frameRate: 24,
      repeat: 0
    });
  }

  private createAnimation(sheet: SpriteSheetDefinition) {
    const key = this.animationKey(sheet.key);

    this.recreateAnimation(key, {
      key,
      frames: this.anims.generateFrameNumbers(this.textureKey(sheet.key), { start: 0, end: sheet.frames - 1 }),
      frameRate: sheet.frameRate,
      repeat: sheet.repeat
    });
  }

  private recreateAnimation(key: string, config: Phaser.Types.Animations.Animation) {
    if (this.anims.exists(key)) {
      this.anims.remove(key);
    }

    this.anims.create(config);
  }

  private animationKey(value: string) {
    return this.textureKey(`anim-${value}`);
  }

  private textureKey(value: string) {
    return `${this.theme.id}:${this.theme.version}:${value}`;
  }

  private currentFrameIndex(sprite: Phaser.GameObjects.Sprite) {
    const value = Number(sprite.frame.name);
    return Number.isFinite(value) ? value : 0;
  }

  private screenXForTarget(target: ActiveTarget) {
    if (target.screenX !== undefined) return target.screenX;
    return this.screenXForLayerX(target.worldX, target.definition.layerId);
  }

  private screenXForLayerX(layerX: number, layerId?: string) {
    return layerX - this.cameraX * this.layerScrollFactor(layerId);
  }

  private layerScrollFactor(layerId?: string) {
    if (!layerId) return 1;

    const layer = this.theme.backgroundLayers.find((definition) => definition.id === layerId);
    const cameraMax = this.theme.round.worldWidth - this.theme.round.viewportWidth;
    if (!layer || cameraMax <= 0) return 1;

    return (layer.width - this.theme.round.viewportWidth) / cameraMax;
  }
}

function weightedPick<T extends { spawnWeight: number }>(items: T[]): T {
  const total = items.reduce((sum, item) => sum + item.spawnWeight, 0);
  let roll = Math.random() * total;

  for (const item of items) {
    roll -= item.spawnWeight;
    if (roll <= 0) return item;
  }

  return items[items.length - 1];
}

function weightedPickSound(items: SoundKey[]) {
  return items[Math.floor(Math.random() * items.length)];
}

function targetAssets(target: TargetDefinition): SpriteSheetDefinition[] {
  return [target.asset, ...(target.assetVariants ?? []).map((variant) => variant.asset)];
}

function targetDeathAssets(target: TargetDefinition): SpriteSheetDefinition[] {
  return (target.assetVariants ?? []).flatMap((variant) => variant.deathAsset ? [variant.deathAsset] : []);
}

function randomTargetAsset(target: TargetDefinition) {
  const assets = [
    { asset: target.asset, deathAsset: target.deathAsset },
    ...(target.assetVariants ?? [])
  ];
  return assets[Math.floor(Math.random() * assets.length)] ?? assets[0];
}

function randomInRange([min, max]: [number, number]) {
  return min + Math.random() * (max - min);
}

function formatClock(milliseconds: number) {
  const totalSeconds = Math.ceil(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
